import { JoinLocationState } from './form';
import { SocialJoinControllerService } from '@/__generated__/CommonApi';
import SocialLinkPopup from '@/components/Popup/SocialLinkPopup';
import iconFacebook from '@/assets/img/join/ico_facebook@2x.png';
import iconGoogle from '@/assets/img/join/ico_google@2x.png';
import Button from '@/components/Button';
import Row from '@/components/Grid/Row';
import PopupModal from '@/components/PopupModal';
import NaverLogin, {
  NaverSuccessResult,
} from '@/components/SocialLogin/NaverLogin';
import { Tit } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import LayoutWithoutTitle from '@/layouts/LayoutWithoutTitle';
import {
  FormContainer,
  PageTitle,
  SocialButton,
} from '@/page-blocks/auth/AuthCommon';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { navigate, PageProps } from 'gatsby';
import React, { FC, useCallback, useState } from 'react';
import { ReactFacebookLoginInfo } from 'react-facebook-login';
import FacebookLogin from '@greatsumini/react-facebook-login';
import KakaoLogin, {
  KakaoSimpleJoin,
  KakaoUser,
} from '@/components/SocialLogin/KakaoLogin';
import { SocialType } from '@/helpers/TypesHelper';
import { usePopupStore } from '@/stores/PopupStore';
import dayjs from 'dayjs';

// 소셜로그인 인증정보
interface SocialData {
  socialId: string;
  socialType: SocialType;
  email: string | undefined;
  name: string | undefined;
  birthday?: string;
  mobilePhoneNumber?: string;
}

const Join: FC<PageProps> = ({ location }) => {
  const [popupMessage, setPopupMessage] = useState<string>();
  const [existUserType, setExistUserType] = useState<string>();
  const [tmpNavigateParam, setTmpNavigateParam] = useState<JoinLocationState>();
  const [isAlertOpen, setIsAlertOpen] = useState(false); // 카카오 연동 전 안내 alert
  const [fbAccessToken, setFbAccessToken] = useState<string>();
  const [existsJoinCheck, setExistsJoinCheck] = useState(false);
  const popupStore = usePopupStore();
  // KS-2783: 카카오 간편가입 기존 웹ID 팝업 데이터
  const [kakaoExistWebIdData, setKakaoExistWebIdData] = useState<{
    maskedName: string;
    existWebId: string;
    donorId: string;
    socialType: SocialType;
    kakaoInfo: KakaoUser;
  } | null>(null);

  // 소셜 가입 구분
  const requestSocialJoin = (
    isUnique: boolean,
    stateData: React.SetStateAction<JoinLocationState | undefined>,
    socialData: SocialData,
  ) => {
    setExistsJoinCheck(false);
    // TODO : 로그인시 입력 받은 데이터로 바로 가입처리
    console.log('>>>>> requestSocialJoin : ', isUnique, stateData, socialData);
    if (isUnique) {
      navigate(`/auth/join/form`, {
        state: stateData,
      });
      /*
      if (
        socialData.socialId &&
        socialData.name &&
        socialData.email &&
        socialData.birthday
      ) {
        // 14세 미만 체크
        const birthday = dayjs(socialData.birthday, `YYYYMMDD`);
        const age = birthday && dayjs().diff(birthday, `year`, true);
        const isUnderFourteen = age !== null && age < 14 && age >= 0;
        if (isUnderFourteen) {
          // 미성년자 보호자 정보 입력 필요
          popupStore.show(`만 14세 미만은 법정대리인 정보 입력이 필요합니다.`, {
            showCancelButton: true,
            onConfirm: (agree) => {
              if (agree) {
                navigate(`/auth/join/form`, {
                  state: stateData,
                });
              }
            },
          });
        } else {
          // 즉시가입
          submitSocialJoinInfo(socialData);
        }
      } else {
        popupStore.show(`필수 정보가 없어 회원가입폼으로 이동합니다.`, {
          showCancelButton: true,
          onConfirm: (agree) => {
            if (agree) {
              navigate(`/auth/join/form`, {
                state: stateData,
              });
            }
          },
        });
      }
      */
    } else {
      // 데이터 임시저장. 오류 메시지는 socialJoinCheck 에서 출력
      setTmpNavigateParam(stateData);
      navigate(`/auth/join`, {
        state: stateData,
      });
    }
  };

  // 소셜가입시 이메일 중복체크
  const socialJoinCheck = useCallback(
    async (email: string, socialId: string, socialType: SocialType) => {
      const {
        data: _data,
      } = await SocialJoinControllerService.socialJoinBeforeEmailDuplicateCheckUsingPost(
        {
          email,
          id: socialId,
          snsDiv: socialType,
        },
      );

      const data = _data as any;

      // 아이디 또는 이메일 중복이면
      if ([`S`, `G`].includes(data.existUserType)) {
        // 오류메세지 띄우기
        setExistUserType(data.existUserType);
        setPopupMessage(data.returnMessage);
        return false;
      }

      return true;
    },
    [],
  );

  // 네이버로 회원가입
  const joinWithNaver = useCallback(
    async (result: NaverSuccessResult) => {
      const socialTypeCd = 'N';
      const naverInfo = result.user;
      const isUnique = await socialJoinCheck(
        naverInfo?.email || '',
        naverInfo?.id || '',
        socialTypeCd,
      );
      requestSocialJoin(
        isUnique,
        {
          naver: result,
        },
        {
          socialId: `${naverInfo?.id}`,
          socialType: socialTypeCd,
          email: naverInfo?.email,
          name: naverInfo?.name,
          birthday: naverInfo?.convertBirthYmd,
          mobilePhoneNumber: naverInfo?.convertPhoneNumber,
        },
      );
    },
    [socialJoinCheck],
  );

  // 페이스북으로 회원가입
  const joinWithFacebook = useCallback(
    async (result: ReactFacebookLoginInfo) => {
      const socialTypeCd = 'F';
      const fbInfo = { ...result, accessToken: fbAccessToken };
      const isUnique = await socialJoinCheck(
        fbInfo.email || '',
        fbInfo.id,
        socialTypeCd,
      );
      if (fbInfo.accessToken) {
        setPopupMessage(`페이스북 로그인에 실패하였습니다.`);
        return;
      }
      requestSocialJoin(
        isUnique,
        {
          facebook: fbInfo,
        },
        {
          socialId: fbInfo.id,
          socialType: socialTypeCd,
          email: fbInfo.email,
          name: fbInfo?.name,
          birthday: '',
          mobilePhoneNumber: '',
        },
      );
    },
    [fbAccessToken, socialJoinCheck],
  );

  // 구글로 회원가입
  const joinWithGoogle = useGoogleLogin({
    onSuccess: async (res) => {
      const socialTypeCd = 'G';
      const userInfo = await axios.get(
        `https://www.googleapis.com/oauth2/v3/userinfo`,
        {
          headers: { Authorization: `Bearer ${res.access_token}` },
          withCredentials: false,
        },
      );
      const googleInfo = userInfo.data;
      const isUnique = await socialJoinCheck(
        googleInfo.email,
        googleInfo.sub,
        socialTypeCd,
      );
      requestSocialJoin(
        isUnique,
        {
          google: googleInfo,
        },
        {
          socialId: googleInfo.sub,
          socialType: socialTypeCd,
          email: googleInfo?.email,
          name: googleInfo?.name,
          birthday: '',
          mobilePhoneNumber: '',
        },
      );
    },
    // eslint-disable-next-line no-console
    onError: (e) => console.error(e),
  });

  // 카카오로 가입하기
  const joinWithKakao = useCallback(async (kakaoInfo: KakaoUser) => {
    // 연락처를 받아오지 못한 경우 안내 후 회원가입 폼으로 이동
    if (!kakaoInfo.convertPhoneNumber) {
      alert(
        '휴대전화 정보가 확인되지 않아 추가 정보 입력이 필요합니다.\n회원가입 페이지로 이동합니다.',
      );
      navigate(`/auth/join/form`, {
        state: { kakao: kakaoInfo },
      });
      return;
    }
    // 간편가입 진행
    const joinResult = await KakaoSimpleJoin(kakaoInfo);
    if (joinResult === null) {
      popupStore.show('간편가입에 실패했습니다. 나중에 다시 시도해주세요.');
    } else {
      if (joinResult.resultCode === 'success') {
        if (joinResult.resultState === 'JOIN') {
          // 신규가입
          navigate(`/auth/join/complete`);
        } else if (joinResult.resultState === 'LINKED') {
          // 계정 연결
          navigate(`/auth/join/complete`);
        }
      } else if (joinResult.resultCode === 'existWebId') {
        // KS-2783: 기존 웹ID 발견 시 팝업 표시
        setKakaoExistWebIdData({
          maskedName: joinResult.maskedName || ``,
          existWebId: joinResult.existWebId || ``,
          donorId: joinResult.donorId || ``,
          socialType: (joinResult.socialType as SocialType) || `K`,
          kakaoInfo,
        });
      } else {
        popupStore.show(`${joinResult.resultMessage}`);
      }
    }
  }, []);

  return (
    <>
      <LayoutWithoutTitle location={location} hideNull>
        <FormContainer
          css={`
            padding-top: 40px;
            ${breakpoint(`mobile`)} {
              padding-top: 48px;
            }
          `}
        >
          <PageTitle
            css={`
              margin: 0 0 48px 0;
            `}
          >
            <h2>
              <Tit size="s1-5" color="sky" weight="normal">
                회원가입
              </Tit>
            </h2>
            <p
              css={`
                ${breakpoint(`mobile`)} {
                  margin-top: 10px !important;
                }
              `}
            >
              유니세프 한국위원회에 오신 것을 환영합니다. <br />
              회원가입 유형을 선택해 주세요.
            </p>
          </PageTitle>
          <Row>
            <Button
              full
              onClick={() => navigate(`/auth/join/form`)}
              css={`
                font-size: 16px;
                height: 55px;
                ${breakpoint(`mobile`)} {
                  font-size: 15px;
                  height: 50px;
                }
              `}
            >
              유니세프 웹회원 가입하기
            </Button>
          </Row>
          <Row
            css={`
              margin-top: 14px;
              ${breakpoint(`mobile`)} {
                margin-top: 10px;
              }
            `}
          >
            <KakaoLogin
              clientId={process.env.KAKAO_JAVASCRIPT_KEY || ``}
              callbackUrl={`${process.env.SELF_URL}/auth/join`}
              isReturnProfile={true}
              role="join"
              render={(props) => (
                <Button {...props} type="button" full sns="kakao">
                  카카오로 시작하기
                </Button>
              )}
              onSuccess={joinWithKakao}
              onFailure={(msg) => console.error(msg)}
            />
          </Row>
          <Row
            css={`
              margin-top: 14px;
              ${breakpoint(`mobile`)} {
                margin-top: 10px;
              }
            `}
          >
            <NaverLogin
              clientId={process.env.NAVER_LOGIN_CLIENT_ID || ``}
              callbackUrl={`${process.env.SELF_URL}/auth/join`}
              isPopup={true}
              render={(props) => (
                <Button type="button" sns="naver" full {...props}>
                  네이버로 시작하기
                </Button>
              )}
              onSuccess={joinWithNaver}
              onFailure={(e) => console.error(e)}
            />
          </Row>
          <Row
            css={`
              margin: 40px 0 0;
              justify-content: center;
              gap: 40px;
              ${breakpoint(`mobile`)} {
                margin: 20px 0 0;
              }
            `}
          >
            <SocialButton onClick={() => joinWithGoogle()}>
              <img src={iconGoogle} alt="구글" width="50" height="50" />
            </SocialButton>
            <FacebookLogin
              appId={process.env.FACEBOOK_LOGIN_APP_ID || ``}
              fields="name,email"
              disableMobileRedirect
              render={({ onClick }) => (
                <SocialButton onClick={onClick}>
                  <img
                    src={iconFacebook}
                    alt="페이스북"
                    width="50"
                    height="50"
                  />
                </SocialButton>
              )}
              dialogParams={{ response_type: 'fbcode' }}
              onSuccess={(response) => {
                setFbAccessToken(response.accessToken);
                //console.log(response);
              }}
              onFail={(_error) => {
                //console.log('Login Failed!', _error);
              }}
              onProfileSuccess={(response) => {
                joinWithFacebook(response);
                //console.log('Get Profile Success!', response);
              }}
            />
          </Row>
        </FormContainer>
      </LayoutWithoutTitle>
      {popupMessage && (
        <PopupModal
          message={popupMessage}
          maxWidth="600px"
          onRequestClose={() => setPopupMessage(undefined)}
          footer={
            <>
              <Button size="sm" outline onClick={() => navigate(`/auth/login`)}>
                기존 아이디로 로그인
              </Button>
              {existUserType === `G` && (
                <>
                  <Button
                    size="sm"
                    outline
                    onClick={() => {
                      navigate(`/auth/find-id`);
                    }}
                  >
                    아이디/비밀번호 찾기
                  </Button>
                </>
              )}
            </>
          }
        />
      )}

      {/* salesforce 카카오 연동 전 안내 alert */}
      {isAlertOpen && (
        <PopupModal
          onRequestClose={() => setIsAlertOpen(false)}
          message="휴대전화 확인을 위해 인증페이지로 이동합니다"
          footer={
            <>
              <Button size="sm" onClick={() => setIsAlertOpen(false)}>
                확인
              </Button>
            </>
          }
        />
      )}
      {/* KS-2783: 카카오 간편가입 기존 웹ID 안내 팝업 */}
      {kakaoExistWebIdData && (
        <SocialLinkPopup
          maskedName={kakaoExistWebIdData.maskedName}
          existWebId={kakaoExistWebIdData.existWebId}
          socialType={kakaoExistWebIdData.socialType}
          onLink={async () => {
            try {
              const joinResult = await SocialJoinControllerService.socialSimpleJoinUsingPost(
                {
                  accessToken: kakaoExistWebIdData.kakaoInfo.accessToken,
                  socialType: `K`,
                  existWebIdConfirm: `Y`,
                  donorId: kakaoExistWebIdData.donorId,
                } as any,
              );
              if (joinResult.resultCode === `success`) {
                setKakaoExistWebIdData(null);
                navigate(`/auth/join/complete`);
              } else {
                setKakaoExistWebIdData(null);
                popupStore.show(
                  (joinResult.data as any)?.returnMessage ||
                    `가입에 실패했습니다.`,
                );
              }
            } catch (e) {
              setKakaoExistWebIdData(null);
              popupStore.show(`서버 에러. 관리자에 문의해주세요.`);
            }
          }}
          onRequestClose={() => setKakaoExistWebIdData(null)}
        />
      )}
    </>
  );
};

export default Join;
